import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Bell, Plus, Trash2, Edit2, Thermometer, MapPin, 
  Battery, Clock, Mail, MessageSquare, Globe, Check, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RULE_TYPES = [
  { value: 'temperature_high', label: 'Temperature High', icon: Thermometer, color: 'text-red-600' },
  { value: 'temperature_low', label: 'Temperature Low', icon: Thermometer, color: 'text-blue-600' },
  { value: 'battery_low', label: 'Battery Low', icon: Battery, color: 'text-orange-600' },
  { value: 'location_deviation', label: 'Location Deviation', icon: MapPin, color: 'text-purple-600' },
  { value: 'eta_delay', label: 'ETA Delay', icon: Clock, color: 'text-yellow-600' },
];

const AlertRules = () => {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    device_id: '',
    rule_type: 'temperature_high',
    threshold: 8,
    notify_email: true,
    notify_sms: false,
    notify_webhook: false,
    webhook_url: '',
  });

  // Fetch alert rules
  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['alert-rules'],
    queryFn: async () => {
      const response = await axios.get(`${API}/alert-rules`);
      return response.data;
    },
  });

  // Fetch devices for dropdown
  const { data: devices = [] } = useQuery({
    queryKey: ['devices'],
    queryFn: async () => {
      const response = await axios.get(`${API}/devices`);
      return response.data;
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(`${API}/alert-rules`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['alert-rules']);
      setIsDialogOpen(false);
      resetForm();
      toast.success('Alert rule created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create rule');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await axios.put(`${API}/alert-rules/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['alert-rules']);
      setIsDialogOpen(false);
      setEditingRule(null);
      resetForm();
      toast.success('Alert rule updated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update rule');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await axios.delete(`${API}/alert-rules/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['alert-rules']);
      toast.success('Alert rule deleted');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to delete rule');
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      device_id: '',
      rule_type: 'temperature_high',
      threshold: 8,
      notify_email: true,
      notify_sms: false,
      notify_webhook: false,
      webhook_url: '',
    });
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      device_id: rule.device_id || '',
      rule_type: rule.rule_type,
      threshold: rule.threshold,
      notify_email: rule.notify_email,
      notify_sms: rule.notify_sms,
      notify_webhook: rule.notify_webhook,
      webhook_url: rule.webhook_url || '',
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      device_id: formData.device_id === 'all' ? null : formData.device_id || null,
      threshold: parseFloat(formData.threshold),
    };

    if (editingRule) {
      updateMutation.mutate({ id: editingRule.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const getRuleTypeInfo = (ruleType) => {
    return RULE_TYPES.find(r => r.value === ruleType) || RULE_TYPES[0];
  };

  const getThresholdUnit = (ruleType) => {
    switch (ruleType) {
      case 'temperature_high':
      case 'temperature_low':
        return '°C';
      case 'battery_low':
        return '%';
      case 'location_deviation':
        return 'km';
      case 'eta_delay':
        return 'hours';
      default:
        return '';
    }
  };

  return (
    <div className="space-y-6" data-testid="alert-rules-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">Custom Alert Rules</h2>
          <p className="text-slate-600 mt-1">Create and manage automated alert notifications</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setEditingRule(null); setIsDialogOpen(true); }}
          className="bg-gradient-to-r from-blue-600 to-cyan-500"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Rule
        </Button>
      </div>

      {/* Rules List */}
      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-slate-500">Loading rules...</p>
        </div>
      ) : rules.length === 0 ? (
        <Card>
          <CardContent className="pt-12 pb-12">
            <div className="text-center">
              <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No alert rules</h3>
              <p className="text-slate-600 mb-6">Create your first custom alert rule to get notified</p>
              <Button onClick={() => setIsDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Rule
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rules.map((rule) => {
            const typeInfo = getRuleTypeInfo(rule.rule_type);
            const Icon = typeInfo.icon;
            
            return (
              <Card key={rule.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${typeInfo.color}`} />
                      </div>
                      <div>
                        <CardTitle className="text-base">{rule.name}</CardTitle>
                        <CardDescription className="text-xs">
                          {rule.device_id ? `Device: ${rule.device_id}` : 'All devices'}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge className={rule.enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                      {rule.enabled ? 'Active' : 'Disabled'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-600">Threshold</span>
                      <span className="font-mono font-semibold">
                        {rule.threshold}{getThresholdUnit(rule.rule_type)}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Notify via:</span>
                      {rule.notify_email && (
                        <div className="p-1 bg-blue-100 rounded" title="Email">
                          <Mail className="w-3 h-3 text-blue-600" />
                        </div>
                      )}
                      {rule.notify_sms && (
                        <div className="p-1 bg-green-100 rounded" title="SMS">
                          <MessageSquare className="w-3 h-3 text-green-600" />
                        </div>
                      )}
                      {rule.notify_webhook && (
                        <div className="p-1 bg-purple-100 rounded" title="Webhook">
                          <Globe className="w-3 h-3 text-purple-600" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2 pt-2 border-t border-slate-200">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleEdit(rule)}
                      >
                        <Edit2 className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => deleteMutation.mutate(rule.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-blue-600" />
              {editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}
            </DialogTitle>
            <DialogDescription>
              Configure conditions and notification channels
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Rule Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Rule Name</Label>
              <Input
                id="name"
                placeholder="e.g., Cold Storage High Temp"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            {/* Device Selection */}
            <div className="space-y-2">
              <Label htmlFor="device">Device (optional)</Label>
              <Select 
                value={formData.device_id} 
                onValueChange={(value) => setFormData({ ...formData, device_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All devices" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All devices</SelectItem>
                  {devices.map((device) => (
                    <SelectItem key={device.device_id} value={device.device_id}>
                      {device.name} ({device.device_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Rule Type */}
            <div className="space-y-2">
              <Label>Alert Type</Label>
              <Select 
                value={formData.rule_type}
                onValueChange={(value) => setFormData({ ...formData, rule_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RULE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className={`w-4 h-4 ${type.color}`} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Threshold */}
            <div className="space-y-2">
              <Label htmlFor="threshold">
                Threshold ({getThresholdUnit(formData.rule_type)})
              </Label>
              <Input
                id="threshold"
                type="number"
                step="0.1"
                value={formData.threshold}
                onChange={(e) => setFormData({ ...formData, threshold: e.target.value })}
                required
              />
            </div>

            {/* Notification Channels */}
            <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
              <Label className="text-sm font-semibold">Notification Channels</Label>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-500" />
                  <span className="text-sm">Email</span>
                </div>
                <Switch
                  checked={formData.notify_email}
                  onCheckedChange={(checked) => setFormData({ ...formData, notify_email: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-slate-500" />
                  <span className="text-sm">SMS</span>
                </div>
                <Switch
                  checked={formData.notify_sms}
                  onCheckedChange={(checked) => setFormData({ ...formData, notify_sms: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-slate-500" />
                  <span className="text-sm">Webhook</span>
                </div>
                <Switch
                  checked={formData.notify_webhook}
                  onCheckedChange={(checked) => setFormData({ ...formData, notify_webhook: checked })}
                />
              </div>
              
              {formData.notify_webhook && (
                <Input
                  placeholder="https://your-webhook-url.com"
                  value={formData.webhook_url}
                  onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                  className="mt-2"
                />
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-500"
              >
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AlertRules;
